import {
  Children,
  cloneElement,
  ComponentType,
  createContext,
  forwardRef,
  isValidElement,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { To, useNavigate } from 'react-router';

import { useScrollState } from '@components/patterns/DataTable/scroll';
import {
  isElementOfType,
  isInteractiveElement,
} from '@components/patterns/DataTable/utilities';
import { Button } from '@components/primitives/Button';
import { ConnectedFields } from '@components/primitives/ConnectedFields';
import { FocusRing } from '@components/primitives/FocusRing';
import { InfiniteScroll } from '@components/primitives/InfiniteScroll';
import { Link } from '@components/primitives/Link';
import { Select } from '@components/primitives/Select';
import { Skeleton } from '@components/primitives/Skeleton';
import { cn } from '@components/primitives/utilities';

import pluralize from '@utils/pluralize';

import css from './styles.module.css';

interface DataTableContextValue {
  headings: Omit<HeadingProps, 'children'>[];
}
const DataTableContext = createContext<DataTableContextValue>({
  headings: [],
});

interface EmptyProps extends PropsWithChildren {
  className?: string;
}
function Empty({ children, className }: EmptyProps) {
  return <div className={cn(css.empty, className)}>{children}</div>;
}

export interface DataTablePaginationProps {
  disabled?: boolean;
  page: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  label?: string | { singular: string; plural: string };
}
function Pagination({
  page,
  total,
  pageSize,
  onPageChange,
  disabled,
  label,
}: DataTablePaginationProps) {
  const totalPages = Math.ceil(total / pageSize);

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  const isDisabled = total === 0 || disabled;

  if (page === 1 && totalPages <= 0) {
    return null;
  }

  return (
    <div className={css.pagination}>
      {total > 0 && (
        <p className={css.paginationText}>
          {startItem}
          <span className={css.enDash}>&ndash;</span>
          {endItem} of {label ? pluralize(total, label) : total}
        </p>
      )}

      <ConnectedFields>
        <Button
          size="sm"
          aria-label="Previous page"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1 || isDisabled}
        >
          <ChevronLeft aria-hidden />
        </Button>

        <Select
          trigger={
            <Button aria-label="Change page" size="sm">
              {page}
            </Button>
          }
          disabled={totalPages === 1 || isDisabled}
          value={page.toString()}
          onChange={(value) => onPageChange(Number(value))}
        >
          {Array.from({ length: totalPages }, (_, index) => index + 1).map(
            (page) => (
              <Select.Option key={page} value={page.toString()}>
                {page}
              </Select.Option>
            )
          )}
        </Select>

        <Button
          size="sm"
          aria-label="Next page"
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages || isDisabled}
        >
          <ChevronRight aria-hidden />
        </Button>
      </ConnectedFields>
    </div>
  );
}

interface LoadingProps {
  count?: number;
}
function Loading({ count = 10 }: LoadingProps) {
  const rows = useMemo(
    () => Array.from({ length: count }, (_, index) => index),
    [count]
  );

  return (
    <div className={css.loading}>
      {rows.map((index) => (
        <div
          key={index}
          className={css.loadingItem}
          style={{ '--z-index': count - index }}
        >
          <Skeleton width="calc(100% - 28px)" height="12px" />
        </div>
      ))}
    </div>
  );
}

interface HeadingProps extends PropsWithChildren {
  hidden?: boolean;
  align?: 'start' | 'center' | 'end';
  collapse?: boolean;
  truncate?: boolean;
  size?: number | string;
}
const DEFAULT_HEADING_PROPS: HeadingProps = {
  align: 'start',
  collapse: false,
};
function Heading({ children, align, collapse, hidden = false }: HeadingProps) {
  return (
    <th
      className={css.th}
      data-align={align ?? DEFAULT_HEADING_PROPS.align}
      data-collapse={collapse ?? DEFAULT_HEADING_PROPS.collapse}
      scope="col"
      role="columnheader"
      data-hidden={hidden}
    >
      <span className={cn(hidden && 'sr-only')}>{children}</span>
    </th>
  );
}

interface HeadProps extends PropsWithChildren {
  hidden?: boolean;
}
function Head({ children, hidden }: HeadProps) {
  return (
    <thead className={cn(css.thead, hidden && 'sr-only')}>
      <tr className={css.tr}>{children}</tr>
    </thead>
  );
}

interface CellProps extends PropsWithChildren {
  className?: string;
  index: number;
  href?: string;
  state?: any;
  onClick?(evt: MouseEvent<HTMLElement>): void;
  icon?: ReactNode;
  suffix?: ReactNode;
  disabled?: boolean;
}
interface Cell {
  focus(): void;
}
const Cell = forwardRef<Cell, CellProps>(function Cell(
  { children, index, href, state, onClick, icon, suffix, className, disabled },
  ref
) {
  const { headings } = useContext(DataTableContext);
  const heading = headings[index];
  const linkRef = useRef<HTMLAnchorElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(
    (evt: MouseEvent<HTMLElement>) => {
      evt.stopPropagation();
      onClick?.(evt);
    },
    [onClick]
  );
  useImperativeHandle(ref, () => ({
    focus() {
      linkRef.current?.focus();
      buttonRef.current?.focus();
    },
  }));

  const content = (
    <div className={cn(css.cell, className)}>
      {icon && <span className={css.icon}>{icon}</span>}
      <span className={css.cellText}>{children}</span>
      {suffix && <span className={css.suffix}>{suffix}</span>}
    </div>
  );

  return (
    <td
      className={css.td}
      data-align={heading.align ?? DEFAULT_HEADING_PROPS.align}
      data-collapse={heading.collapse ?? DEFAULT_HEADING_PROPS.collapse}
      data-truncate={heading.truncate ?? false}
      role="cell"
    >
      {index === 0 && href && !disabled ? (
        <FocusRing>
          <Link ref={linkRef} href={href} state={state} onClick={handleClick}>
            {content}
          </Link>
        </FocusRing>
      ) : index === 0 && onClick ? (
        <FocusRing>
          <button
            ref={buttonRef}
            type="button"
            disabled={disabled}
            onClick={handleClick}
          >
            {content}
          </button>
        </FocusRing>
      ) : (
        content
      )}
    </td>
  );
});

interface RowProps extends PropsWithChildren {
  className?: string;
  zIndex: number;
  href?: To;
  state?: any;
  onClick?(evt: MouseEvent<HTMLElement>): void;
  disabled?: boolean;
}
interface Row {
  focus(): void;
}
const Row = forwardRef<Row, RowProps>(function Row(
  {
    children,
    className,
    zIndex,
    href,
    state,
    onClick,
    disabled = false,
    ...props
  },
  ref
) {
  const navigate = useNavigate();

  const handleClick = useCallback(
    (evt: MouseEvent<HTMLTableRowElement>) => {
      if (disabled) return;
      onClick?.(evt);
      if (evt.defaultPrevented) return;
      if (isInteractiveElement(document.activeElement)) return;
      if (!href) return;
      void navigate(href, { state });
    },
    [onClick, href, state, navigate]
  );

  const validChildren = Children.toArray(children).filter((child) =>
    isValidElement(child)
  );

  return (
    <tr
      className={cn(css.tr, className)}
      role="row"
      data-interactive={!!onClick || !!href}
      data-disabled={disabled}
      onClick={handleClick}
      style={{ '--z-index': zIndex }}
      {...props}
    >
      {validChildren.map((child, index) => {
        return cloneElement(child, {
          ref: index === 0 ? ref : undefined,
          index,
          href,
          state,
          onClick,
          disabled,
        } as CellProps);
      })}
    </tr>
  );
});

interface BodyProps extends PropsWithChildren {}
function Body({ children }: BodyProps) {
  const count = Children.count(children);

  return (
    <tbody className={css.tbody}>
      {Children.map(children, (child, index) => {
        if (!isValidElement(child)) return null;
        return cloneElement(child, { zIndex: count - index } as RowProps);
      })}
    </tbody>
  );
}

interface FooterProps extends PropsWithChildren {
  className?: string;
}
function Footer({ children, className }: FooterProps) {
  return <div className={cn(css.footer, className)}>{children}</div>;
}

interface RootProps extends PropsWithChildren {
  className?: string;
  contained?: boolean;
  onEndReached?: () => void;
  density?: 'compact' | 'default';
}
function Root({
  children,
  className,
  contained = true,
  onEndReached,
  density = 'default',
}: RootProps) {
  const childrenArray = Children.toArray(children);
  const head = childrenArray.find((child) => isElementOfType(child, Head));
  const body = childrenArray.find((child) => isElementOfType(child, Body));
  const footer = childrenArray.find((child) => isElementOfType(child, Footer));
  const empty = childrenArray.find((child) => isElementOfType(child, Empty));
  const loading = childrenArray.find((child) =>
    isElementOfType(child, Loading)
  );
  const pagination = childrenArray.find((child) =>
    isElementOfType(child, Pagination)
  );
  const slots: unknown[] = [head, body, footer, empty, loading, pagination];
  const rest = childrenArray.filter((child) => !slots.includes(child));
  const context = useMemo<DataTableContextValue>(() => {
    if (!head) {
      return { headings: [] };
    }

    const children = (head as ReactElement).props.children;
    const headings = Children.toArray(children).flatMap((child) => {
      if (isElementOfType(child, Heading)) {
        return child.props;
      } else {
        return [];
      }
    });

    return { headings };
  }, [head]);

  const gridTemplateColumns = useMemo(() => {
    if (!context.headings.length) return 'auto';
    return context.headings
      .map((heading) => {
        if (heading.collapse) return 'auto';
        if (typeof heading.size === 'string') return heading.size;
        const size = `${heading.size ?? 1}fr`;
        return heading.truncate ? `minmax(0, ${size})` : size;
      })
      .join(' ');
  }, [context.headings]);

  const ref = useRef<HTMLDivElement>(null);
  const scroll = useScrollState(ref);

  return (
    <div
      className={cn(css.indicators, className)}
      data-can-scroll-x={scroll.canScrollX}
      data-can-scroll-y={scroll.canScrollY}
      data-scroll-x-position={scroll.scrollXPosition}
      data-scroll-y-position={scroll.scrollYPosition}
    >
      <div
        ref={ref}
        className={css.container}
        data-contained={contained}
        data-density={density}
      >
        <table
          className={css.table}
          role="table"
          data-truncate={context.headings.some((heading) => heading.truncate)}
          style={{ '--table-columns': gridTemplateColumns }}
        >
          {head}

          <DataTableContext.Provider value={context}>
            {body}
          </DataTableContext.Provider>
        </table>

        {rest}

        {loading}

        {onEndReached && <InfiniteScroll fetchNextPage={onEndReached} />}

        {empty}
      </div>

      {(footer || pagination) && (
        <div className={css.footerContainer}>
          {footer}
          {pagination}
        </div>
      )}
    </div>
  );
}

export type DataTableCell = Cell;

export const DataTable = Object.assign(Root, {
  Head,
  Heading,
  Body,
  Footer,
  Row: Row as unknown as ComponentType<
    Omit<RowProps, 'zIndex'> & { ref?: Ref<Row> }
  >,
  Cell: Cell as unknown as ComponentType<
    Omit<CellProps, 'index' | 'href' | 'onClick'> & { ref?: Ref<Cell> }
  >,
  Loading,
  Empty,
  Pagination,
});
